import { useInsightsAvailableWorkflows } from '@/utils/api';
import { signOut, useFirebaseAuth } from '@/utils/firebase';
import { Box, Stack, Tab, Tabs, Typography, useMediaQuery, type Theme } from '@mui/material';
import { SignInIcon, SignOutIcon } from '@phosphor-icons/react';
import { IconButton, PRIMARY_COLOR } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ImpersonationBanner, { IMPERSONATION_BANNER_HEIGHT } from '../components/ImpersonationBanner.js';
import Logo from '../components/Logo.js';
import { ImpersonationProvider, useImpersonation } from '../context/ImpersonationContext.js';
import { useCurrentZubeAgentUser } from '../utils/api.js';
import AgentMobileLayout from './AgentMobileLayout.js';
import AgentSidebar, { SIDEBAR_WIDTH } from './AgentSidebar.js';
import MagicLoading from './MagicLoading.js';

const NAVBAR_HEIGHT = 64;

// Map routes to page titles
const getPageTitle = (pathname: string): string => {
  if (pathname === '/agent' || pathname === '/agent/') return 'Home';
  if (pathname.startsWith('/agent/contacts/')) return 'Contact Details';
  if (pathname.startsWith('/agent/contacts')) return 'Contacts';
  if (pathname.startsWith('/agent/insights')) return 'Insights';
  if (pathname.startsWith('/agent/nexus')) return 'Nexus';
  if (pathname.startsWith('/agent/settings')) return 'Integrations';
  if (pathname.startsWith('/agent/account')) return 'Account Settings';
  if (pathname.startsWith('/agent/reminders')) return 'Reminders';
  if (pathname.startsWith('/agent/notifications')) return 'Notifications';
  return 'Dashboard';
};

// Insights sub-navigation tabs configuration
type InsightsTab = { label: string; path: string };

const BASE_INSIGHTS_TABS: InsightsTab[] = [
  { label: 'Overview', path: '/agent/insights' },
  { label: 'Performance', path: '/agent/insights/performance' },
  { label: 'Content', path: '/agent/insights/content' },
  { label: 'Time Patterns', path: '/agent/insights/time-patterns' },
  { label: 'Categories', path: '/agent/insights/categories' },
];

// Workflows tab - only shown when user has multiple workflows
const WORKFLOWS_TAB: InsightsTab = { label: 'Workflows', path: '/agent/insights/workflows' };

function AgentAppLayoutInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const { user: firebaseUser, isInitialized } = useFirebaseAuth();
  const queryClient = useQueryClient();
  const { isImpersonating } = useImpersonation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // Check authentication
  const {
    data: agentUser,
    isLoading,
    isError,
    isFetched,
    fetchStatus,
    refetch,
  } = useCurrentZubeAgentUser({
    enabled: isInitialized && !!firebaseUser,
    showErrorSnackbar: false
  });

  useEffect(() => {
    if (!isInitialized || !firebaseUser) return;

    // If there's an error fetching the agent user, redirect to login
    if (isError) {
      navigate('/agent/auth/login');
      return;
    }
  }, [firebaseUser, isError, isInitialized, navigate]);

  useEffect(() => {
    if (!isInitialized || !firebaseUser) return;
    if (agentUser?.data || isError || isLoading) return;
    if (isFetched || fetchStatus !== 'idle') return;

    void refetch();
  }, [agentUser?.data, fetchStatus, firebaseUser, isError, isFetched, isInitialized, isLoading, refetch]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      queryClient.clear();
      navigate('/agent/auth/login');
    }
  }, [navigate, queryClient]);

  // Insights section detection and sub-navigation
  const isInsightsSection = location.pathname.startsWith('/agent/insights');

  // Fetch workflows to determine if Workflows tab should be shown
  const { data: workflowsResult } = useInsightsAvailableWorkflows({
    enabled: isInsightsSection && isInitialized && !!firebaseUser,
  });
  const workflows = workflowsResult?.data ?? [];
  const showWorkflowsTab = workflows.length > 1;

  // Build insights tabs list
  const insightsTabs = useMemo(() => {
    if (!showWorkflowsTab) return BASE_INSIGHTS_TABS;
    // Insert Workflows tab after Content (index 2)
    const tabs = [...BASE_INSIGHTS_TABS];
    tabs.splice(3, 0, WORKFLOWS_TAB);
    return tabs;
  }, [showWorkflowsTab]);

  // Calculate active tab index for insights
  const activeInsightsTab = useMemo(() => {
    if (!isInsightsSection) return 0;
    const tabIndex = insightsTabs.findIndex((tab) => tab.path === location.pathname);
    return tabIndex === -1 ? 0 : tabIndex;
  }, [isInsightsSection, insightsTabs, location.pathname]);

  const handleInsightsTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const tab = insightsTabs[newValue];
      if (tab) {
        navigate(tab.path);
      }
    },
    [insightsTabs, navigate]
  );

  if (isInitialized && !firebaseUser) {
    return <Navigate to="/agent/auth/login" replace />;
  }

  // Show loading while checking auth
  if (isLoading || !isInitialized || !agentUser?.data) {
    return (
      <MagicLoading
        active
        onMagic={() => navigate('/agent/auth/login')}
      />
    );
  }

  // Mobile gate: redirect all agent pages to notifications on small screens
  if (isMobile) {
    if (location.pathname !== '/agent/notifications') {
      return <Navigate to="/agent/notifications" replace />;
    }
    return <AgentMobileLayout />;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        backgroundColor: PRIMARY_COLOR.hex,
        overflow: 'hidden',
        paddingTop: isImpersonating ? `${IMPERSONATION_BANNER_HEIGHT}px` : 0,
      }}
    >
      <ImpersonationBanner />
      <Stack direction="column" sx={{ height: '100%' }}>
        {/* Top Navbar */}
        <Box
          sx={{
            height: NAVBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            pr: 3,
          }}
        >
          {/* Logo Area */}
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              display: 'flex',
              alignItems: 'center',
              px: 3,
            }}
          >
            <Logo useWhiteLogo height={36} />
          </Box>

          {/* Page Title */}
          <Box sx={{ px: 4, flexShrink: 0 }}>
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: '1.75rem',
              }}
            >
              {pageTitle}
            </Typography>
          </Box>

          {/* Insights Sub-Navigation */}
          {isInsightsSection && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
              <Tabs
                value={activeInsightsTab}
                onChange={handleInsightsTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 'unset',
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: 'white',
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    minHeight: NAVBAR_HEIGHT,
                    px: 2,
                    '&:hover': {
                      color: 'white',
                    },
                    '&.Mui-selected': {
                      color: 'white',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                {insightsTabs.map((tab) => (
                  <Tab key={tab.path} label={tab.label} />
                ))}
              </Tabs>
            </Box>
          )}

          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
            <IconButton
              tooltip={firebaseUser ? 'Log out' : 'Go to login'}
              contained
              onClick={firebaseUser ? handleLogout : () => navigate('/agent/auth/login')}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.24)',
                }
              }}
            >
              {firebaseUser ? <SignOutIcon size={24} /> : <SignInIcon size={24} />}
            </IconButton>
          </Box>
        </Box>

        {/* Bottom Stack - Sidebar + Main Content */}
        <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Sidebar */}
          <Box sx={{ width: SIDEBAR_WIDTH, flexShrink: 0 }}>
            <AgentSidebar />
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
            {/* White Background Container with Rounded Corner */}
            <Box
              sx={{
                flex: 1,
                borderTopLeftRadius: '24px',
                backgroundColor: 'white',
                overflow: 'hidden',
                display: 'flex',
              }}
            >
              {/* Gradient Overlay - Fixed, Non-Scrolling */}
              <Box
                sx={{
                  flex: 1,
                  background: `linear-gradient(to bottom right, rgba(${PRIMARY_COLOR.rgb}, 0.03) 0%, rgba(${PRIMARY_COLOR.rgb}, 0.1) 40%,  rgba(${PRIMARY_COLOR.rgb}, 0.15) 60%, rgba(${PRIMARY_COLOR.rgb}, 0.2) 80%, rgba(${PRIMARY_COLOR.rgb}, 0.4) 100%)`,
                  overflow: 'auto',
                }}
              >
                {/* Scrollable Content */}
                <Box component="main" p={4} sx={{ minWidth: 0 }}>
                  <Outlet />
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function AgentAppLayout() {
  return (
    <ImpersonationProvider>
      <AgentAppLayoutInner />
    </ImpersonationProvider>
  );
}
